import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  Avatar,
  Stack,
  FormControlLabel,
  Switch,
  Chip
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Work as WorkIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { Contact } from '../types/Contact';
import { canFormatPhoneNumber } from '../utils/phoneFormatter';

interface ContactDialogProps {
  open: boolean;
  contact: Contact | null;
  isEditing: boolean;
  readonly?: boolean;  // Neue Prop für readonly-Mode
  onClose: () => void;
  onSave: (contactData: Partial<Contact>) => void;
}

const ContactDialog: React.FC<ContactDialogProps> = ({
  open,
  contact,
  isEditing,
  readonly = false,  // Neue Prop mit Default-Wert
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<Partial<Contact>>(() => {
    if (contact) {
      return { ...contact };
    }
    return {
      // contactid wird automatisch vergeben, also nicht im Formular setzen
      firstname: '',
      lastname: '',
      companyname: '',
      email: '',
      phonemobile: '',
      phonemobile2: '',
      phonehome: '',
      phonehome2: '',
      phonebusiness: '',
      phonebusiness2: '',
      phoneother: '',
      faxbusiness: '',
      faxhome: '',
      pager: '',
      photourl: '',
      aktiv: true,       // Standard: aktiv
      public: false      // Standard: privat
    };
  });

  // Zustand für ungültige Telefonnummern-Felder
  const [invalidPhoneFields, setInvalidPhoneFields] = useState<Set<string>>(new Set());

  // Validiere alle Telefonnummern-Felder
  const validatePhoneFields = (currentFormData: Partial<Contact>) => {
    const phoneFields = [
      'phonemobile', 'phonemobile2', 'phonehome', 'phonehome2', 
      'phonebusiness', 'phonebusiness2', 'phoneother', 'faxbusiness', 'faxhome', 'pager'
    ];
    
    const invalidFields = new Set<string>();
    
    phoneFields.forEach(field => {
      const phoneNumber = currentFormData[field as keyof Contact] as string;
      if (phoneNumber && !canFormatPhoneNumber(phoneNumber)) {
        console.log(`❌ Ungültige Telefonnummer in Feld '${field}': '${phoneNumber}'`);
        invalidFields.add(field);
      }
    });
    
    setInvalidPhoneFields(invalidFields);
    console.log(`📞 Telefonnummer-Validierung: ${invalidFields.size} ungültige Felder gefunden`, Array.from(invalidFields));
    return invalidFields.size === 0;
  };

  useEffect(() => {
    if (contact) {
      setFormData({ ...contact });
      // Beim Öffnen des Dialogs alle Telefonnummern validieren
      validatePhoneFields({ ...contact });
    } else {
      const newFormData = {
        // contactid wird automatisch vergeben, also nicht im Formular setzen
        firstname: '',
        lastname: '',
        companyname: '',
        email: '',
        phonemobile: '',
        phonemobile2: '',
        phonehome: '',
        phonehome2: '',
        phonebusiness: '',
        phonebusiness2: '',
        phoneother: '',
        faxbusiness: '',
        faxhome: '',
        pager: '',
        photourl: '',
        aktiv: true,       // Standard: aktiv
        public: false      // Standard: privat
      };
      setFormData(newFormData);
      // Für neue Kontakte sind alle Felder leer, also keine Validierung nötig
      setInvalidPhoneFields(new Set());
    }
  }, [contact, open]);

  const handleChange = (field: keyof Contact) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    const updatedFormData = {
      ...formData,
      [field]: newValue
    };
    setFormData(updatedFormData);
    
    // Wenn es ein Telefonnummer-Feld ist, validiere es nach der Eingabe
    const phoneFields = [
      'phonemobile', 'phonemobile2', 'phonehome', 'phonehome2', 
      'phonebusiness', 'phonebusiness2', 'phoneother', 'faxbusiness', 'faxhome', 'pager'
    ];
    
    if (phoneFields.includes(field as string)) {
      // Validiere nur dieses spezifische Feld
      const newInvalidFields = new Set(invalidPhoneFields);
      
      if (newValue && !canFormatPhoneNumber(newValue)) {
        newInvalidFields.add(field as string);
      } else {
        newInvalidFields.delete(field as string);
      }
      
      setInvalidPhoneFields(newInvalidFields);
    }
  };

  const handleSwitchChange = (field: keyof Contact) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleSave = () => {
    // Prüfe ob entweder Nachname ODER Firmenname vorhanden ist (Vorname ist optional)
    const hasLastName = formData.lastname?.trim();
    const hasCompanyName = formData.companyname?.trim();
    
    if (!hasLastName && !hasCompanyName) {
      return; // Speichern verhindern
    }
    
    // Alle Telefonnummern nochmal validieren vor dem Speichern
    const isPhoneValidationOk = validatePhoneFields(formData);
    
    // Wenn Telefonnummern-Validierung fehlschlägt, Speichern verhindern
    if (!isPhoneValidationOk) {
      console.log('Speichern verhindert: Ungültige Telefonnummern gefunden');
      return;
    }
    
    // contactid aus den Formulardaten entfernen, da es schreibgeschützt ist
    const { contactid, ...dataToSave } = formData;
    onSave(dataToSave);
  };

  const getFullName = () => {
    return `${formData.firstname || ''} ${formData.lastname || ''}`.trim();
  };

  // Hilfsfunktion für TextField-Props mit readonly-Unterstützung
  const getTextFieldProps = (fieldName: keyof Contact, baseProps: any = {}) => {
    const isReadonly = readonly || (contact?.readonly === true) || (contact?.agrarmonitor === true);
    
    return {
      ...baseProps,
      disabled: isReadonly,
      onChange: isReadonly ? undefined : handleChange(fieldName),
      InputProps: {
        ...baseProps.InputProps,
        readOnly: isReadonly,
        sx: {
          ...baseProps.InputProps?.sx,
          ...(isReadonly && {
            backgroundColor: 'grey.100',
            '& .MuiInputBase-input.Mui-disabled': {
              color: 'text.secondary',
              WebkitTextFillColor: 'unset'
            }
          })
        }
      }
    };
  };

  // Hilfsfunktion für Telefonnummer-TextField-Props
  const getPhoneFieldProps = (fieldName: string) => {
    const isInvalid = invalidPhoneFields.has(fieldName);
    const isReadonly = readonly || (contact?.readonly === true) || (contact?.agrarmonitor === true);
    
    if (isInvalid) {
      return {
        error: true,
        helperText: 'Ungültiges Telefonnummer-Format',
        disabled: isReadonly,
        onChange: isReadonly ? undefined : handleChange(fieldName as keyof Contact),
        sx: { 
          '& .MuiOutlinedInput-root': { 
            '& fieldset': { borderColor: 'orange' },
            '&:hover fieldset': { borderColor: 'orange' },
            '&.Mui-focused fieldset': { borderColor: 'orange' }
          },
          ...(isReadonly && {
            backgroundColor: 'grey.100',
            '& .MuiInputBase-input.Mui-disabled': {
              color: 'text.secondary',
              WebkitTextFillColor: 'unset'
            }
          })
        }
      };
    }
    return {
      error: false,
      helperText: '',
      disabled: isReadonly,
      onChange: isReadonly ? undefined : handleChange(fieldName as keyof Contact),
      InputProps: {
        readOnly: isReadonly,
        sx: isReadonly ? {
          backgroundColor: 'grey.100',
          '& .MuiInputBase-input.Mui-disabled': {
            color: 'text.secondary',
            WebkitTextFillColor: 'unset'
          }
        } : undefined
      }
    };
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar 
            src={formData.photourl || undefined}
            sx={{ width: 56, height: 56 }}
          >
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="h6">
              {readonly || (contact?.readonly === true) || (contact?.agrarmonitor === true) 
                ? 'Kontakt anzeigen (schreibgeschützt)' 
                : isEditing ? 'Kontakt bearbeiten' : 'Neuen Kontakt hinzufügen'
              }
            </Typography>
            {getFullName() && (
              <Typography variant="body2" color="textSecondary">
                {getFullName()}
              </Typography>
            )}
            {(contact?.agrarmonitor === true || contact?.readonly === true) && (
              <Chip
                label="Agrarmonitor-Kontakt"
                size="small"
                sx={{
                  mt: 1,
                  backgroundColor: '#4caf50',
                  color: 'white',
                  fontWeight: 'bold'
                }}
              />
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Grunddaten */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="primary" />
              Grunddaten
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Kontakt-ID"
                value={formData.contactid || (isEditing ? 'Wird automatisch generiert' : 'Wird beim Speichern automatisch vergeben')}
                variant="outlined"
                disabled
                helperText="Die Kontakt-ID wird automatisch vom System vergeben und kann nicht geändert werden"
                InputProps={{
                  sx: { 
                    backgroundColor: 'grey.100',
                    '& .MuiInputBase-input.Mui-disabled': {
                      color: 'text.secondary',
                      WebkitTextFillColor: 'unset'
                    }
                  }
                }}
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Vorname"
                  value={formData.firstname || ''}
                  variant="outlined"
                  {...getTextFieldProps('firstname')}
                />
                <TextField
                  fullWidth
                  label="Nachname *"
                  value={formData.lastname || ''}
                  variant="outlined"
                  {...getTextFieldProps('lastname')}
                  helperText="Entweder Nachname oder Firmenname muss angegeben werden"
                />
              </Box>

              <TextField
                fullWidth
                label="Firmenname *"
                value={formData.companyname || ''}
                variant="outlined"
                {...getTextFieldProps('companyname', {
                  InputProps: {
                    startAdornment: <BusinessIcon sx={{ mr: 1, color: 'action.active' }} />
                  }
                })}
                helperText="Entweder Nachname oder Firmenname muss angegeben werden"
              />

              <TextField
                fullWidth
                label="E-Mail"
                type="email"
                value={formData.email || ''}
                variant="outlined"
                {...getTextFieldProps('email', {
                  InputProps: {
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
                  }
                })}
              />

              {/* Aktiv-Status Einstellung */}
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(formData.aktiv !== false)} // Default true wenn undefined
                      onChange={readonly || (contact?.readonly === true) || (contact?.agrarmonitor === true) ? undefined : handleSwitchChange('aktiv')}
                      disabled={readonly || (contact?.readonly === true) || (contact?.agrarmonitor === true)}
                      color="success"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {formData.aktiv !== false ? <CheckCircleIcon color="success" /> : <CancelIcon color="action" />}
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {formData.aktiv !== false ? 'Aktiv' : 'Inaktiv'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formData.aktiv !== false 
                            ? 'Kontakt ist aktiv und wird in der Standard-Ansicht angezeigt' 
                            : 'Kontakt ist inaktiv und wird nur bei Filter "Inaktiv/Alle" angezeigt'
                          }
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </Box>

              {/* Sichtbarkeits-Einstellung */}
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(formData.public)}
                      onChange={readonly || (contact?.readonly === true) || (contact?.agrarmonitor === true) ? undefined : handleSwitchChange('public')}
                      disabled={readonly || (contact?.readonly === true) || (contact?.agrarmonitor === true)}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {formData.public ? <PublicIcon color="primary" /> : <LockIcon color="action" />}
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {formData.public ? 'Öffentlich sichtbar' : 'Privat'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formData.public 
                            ? 'Alle Benutzer können diesen Kontakt sehen' 
                            : 'Nur Sie können diesen Kontakt sehen'
                          }
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </Box>

              <TextField
                fullWidth
                label="Foto-URL"
                value={formData.photourl || ''}
                variant="outlined"
                {...getTextFieldProps('photourl')}
              />
            </Stack>
          </Box>

          {/* Telefonnummern */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon color="primary" />
              Telefonnummern
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Handy 1"
                  value={formData.phonemobile || ''}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} />
                  }}
                  {...getPhoneFieldProps('phonemobile')}
                />
                <TextField
                  fullWidth
                  label="Handy 2"
                  value={formData.phonemobile2 || ''}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} />
                  }}
                  {...getPhoneFieldProps('phonemobile2')}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Telefon Privat 1"
                  value={formData.phonehome || ''}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <HomeIcon sx={{ mr: 1, color: 'action.active' }} />
                  }}
                  {...getPhoneFieldProps('phonehome')}
                />
                <TextField
                  fullWidth
                  label="Telefon Privat 2"
                  value={formData.phonehome2 || ''}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <HomeIcon sx={{ mr: 1, color: 'action.active' }} />
                  }}
                  {...getPhoneFieldProps('phonehome2')}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Telefon Geschäft 1"
                  value={formData.phonebusiness || ''}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <WorkIcon sx={{ mr: 1, color: 'action.active' }} />
                  }}
                  {...getPhoneFieldProps('phonebusiness')}
                />
                <TextField
                  fullWidth
                  label="Telefon Geschäft 2"
                  value={formData.phonebusiness2 || ''}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <WorkIcon sx={{ mr: 1, color: 'action.active' }} />
                  }}
                  {...getPhoneFieldProps('phonebusiness2')}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Sonstige Telefonnummer"
                  value={formData.phoneother || ''}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} />
                  }}
                  {...getPhoneFieldProps('phoneother')}
                />
                <TextField
                  fullWidth
                  label="Pager"
                  value={formData.pager || ''}
                  variant="outlined"
                  {...getPhoneFieldProps('pager')}
                />
              </Box>
            </Stack>
          </Box>

          {/* Fax */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Fax
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Fax Geschäft"
                value={formData.faxbusiness || ''}
                variant="outlined"
                {...getPhoneFieldProps('faxbusiness')}
              />
              <TextField
                fullWidth
                label="Fax Privat"
                value={formData.faxhome || ''}
                variant="outlined"
                {...getPhoneFieldProps('faxhome')}
              />
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} size="large">
          {readonly || (contact?.readonly === true) || (contact?.agrarmonitor === true) ? 'Schließen' : 'Abbrechen'}
        </Button>
        {/* Agrarmonitor-Link-Button für readonly-Kontakte */}
        {(contact?.readonly === true || contact?.agrarmonitor === true) && contact?.AgrarmonitorId && (
          <Button 
            onClick={() => window.open(`https://admin7.agrarmonitor.de/kunden/detail/${contact.AgrarmonitorId}`, '_blank')}
            variant="contained" 
            color="primary"
            size="large"
          >
            In Agrarmonitor bearbeiten
          </Button>
        )}
        {/* Save-Button nur für normale Kontakte */}
        {!readonly && !contact?.readonly && !contact?.agrarmonitor && (
          <Button 
            onClick={handleSave} 
            variant="contained" 
            size="large"
            disabled={!(formData.lastname?.trim() || formData.companyname?.trim())}
          >
            {isEditing ? 'Aktualisieren' : 'Hinzufügen'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContactDialog;
